import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  Divider,
  Stepper,
  Step,
  StepLabel,
  FormGroup,
  Alert,
  Snackbar,
  InputAdornment,
  OutlinedInput,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  Send as SendIcon,
  ContentCopy as CopyIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import axios from 'axios';

const QuestionnaireManager = () => {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [emailToSend, setEmailToSend] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [currentQuestionnaire, setCurrentQuestionnaire] = useState({
    id: null,
    title: '',
    description: '',
    categories: [],
    questions: [],
    active: true,
    link_token: '',
    expiry_date: ''
  });

  // Create a function to fetch all data that can be called multiple times
  const fetchAllData = async () => {
    try {
      // Fetch categories
      const categoriesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/categories`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (categoriesResponse.data.success) {
        setCategories(categoriesResponse.data.data);
      }
      
      // Fetch questions
      const questionsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/questions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (questionsResponse.data.success) {
        setQuestions(questionsResponse.data.data);
      }
      
      // Fetch questionnaires
      const questionnairesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/questionnaires`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (questionnairesResponse.data.success) {
        // Ensure each questionnaire has questions and categories properties
        const processedQuestionnaires = questionnairesResponse.data.data.map(questionnaire => {
          // Extract unique category IDs from the questions if categories is not provided
          let categories = questionnaire.categories || [];
          
          // If categories is empty but we have questions with category_id, extract those
          if (categories.length === 0 && questionnaire.questions && questionnaire.questions.length > 0) {
            categories = [...new Set(questionnaire.questions
              .filter(q => q.category_id)
              .map(q => q.category_id))];
          }
          
          return {
            ...questionnaire,
            questions: questionnaire.questions || [],
            categories: categories
          };
        });
        setQuestionnaires(processedQuestionnaires);
      }
      return true;
    } catch (error) {
      console.error('Error fetching data:', error);
      setSnackbar({
        open: true,
        message: 'Error loading data. Please try again.',
        severity: 'error'
      });
      return false;
    }
  };
  
  // Load data when component mounts
  useEffect(() => {
    fetchAllData();
  }, []);
  
  // Handle creating a new questionnaire
  const handleCreateQuestionnaire = async (questionnaireData) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/questionnaires`, 
        questionnaireData,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      // Instead of just adding to the list, reload all questionnaires to ensure data consistency
      await fetchAllData();
      
      setSnackbar({
        open: true,
        message: 'Questionnaire created successfully',
        severity: 'success'
      });
      
      return response.data;
    } catch (error) {
      console.error('Error creating questionnaire:', error);
      setSnackbar({
        open: true,
        message: 'Error creating questionnaire',
        severity: 'error'
      });
      throw error;
    }
  };
  
  // Handle updating an existing questionnaire
  const handleUpdateQuestionnaire = async (questionnaireData) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/questionnaires/${questionnaireData.id}`, 
        questionnaireData,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      // Instead of just updating the list, reload all questionnaires to ensure data consistency
      await fetchAllData();
      
      setSnackbar({
        open: true,
        message: 'Questionnaire updated successfully',
        severity: 'success'
      });
      
      return response.data;
    } catch (error) {
      console.error('Error updating questionnaire:', error);
      setSnackbar({
        open: true,
        message: 'Error updating questionnaire',
        severity: 'error'
      });
      throw error;
    }
  };
  
  // Handle deleting a questionnaire
  const handleDeleteQuestionnaireAPI = async (id) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/questionnaires/${id}`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      // Instead of just removing from the list, reload all questionnaires to ensure data consistency
      await fetchAllData();
      
      setSnackbar({
        open: true,
        message: 'Questionnaire deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting questionnaire:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting questionnaire',
        severity: 'error'
      });
      throw error;
    }
  };
  
  // Handle sending a questionnaire via email
  const handleSendQuestionnaireEmail = async (questionnaireId, email) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/questionnaires/${questionnaireId}/send`, 
        {
          email: email
        },
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      return true;
    } catch (error) {
      console.error('Error sending questionnaire:', error);
      throw error;
    }
  };

  // Get all parent questions (not follow-up questions)
  const getParentQuestions = () => {
    if (!questions || !Array.isArray(questions)) return [];
    
    // Create a set of all follow-up question IDs
    const followUpQuestionIds = new Set();
    
    questions.forEach(question => {
      // Check for legacy follow-up question
      if (question.follow_up_question_id) {
        followUpQuestionIds.add(parseInt(question.follow_up_question_id));
      }
      
      // Check for follow-up rules
      if (question.has_follow_up && Array.isArray(question.follow_up_rules)) {
        question.follow_up_rules.forEach(rule => {
          if (rule.follow_up_question_id) {
            followUpQuestionIds.add(parseInt(rule.follow_up_question_id));
          } else if (rule.question_id) {
            followUpQuestionIds.add(parseInt(rule.question_id));
          }
        });
      }
      
      // Check for pivot relationship follow-up questions
      if (question.pivot && question.pivot.follow_up_question_id) {
        followUpQuestionIds.add(parseInt(question.pivot.follow_up_question_id));
      }
    });
    
    // Return only questions that are not follow-up questions
    return questions.filter(question => !followUpQuestionIds.has(question.id));
  };
  
  // Create a map of questions that have follow-up questions
  const getFollowUpQuestionsMap = () => {
    if (!questions || !Array.isArray(questions)) return new Map();
    
    const followUpMap = new Map();
    
    questions.forEach(question => {
      // Check for legacy follow-up question
      if (question.follow_up_question_id) {
        const followUpId = parseInt(question.follow_up_question_id);
        if (!followUpMap.has(question.id)) {
          followUpMap.set(question.id, new Set());
        }
        followUpMap.get(question.id).add(followUpId);
      }
      
      // Check for follow-up rules
      if (question.has_follow_up && Array.isArray(question.follow_up_rules)) {
        question.follow_up_rules.forEach(rule => {
          let followUpId = null;
          if (rule.follow_up_question_id) {
            followUpId = parseInt(rule.follow_up_question_id);
          } else if (rule.question_id) {
            followUpId = parseInt(rule.question_id);
          }
          
          if (followUpId) {
            if (!followUpMap.has(question.id)) {
              followUpMap.set(question.id, new Set());
            }
            followUpMap.get(question.id).add(followUpId);
          }
        });
      }
      
      // Check for pivot relationship follow-up questions
      if (question.pivot && question.pivot.follow_up_question_id) {
        const followUpId = parseInt(question.pivot.follow_up_question_id);
        if (!followUpMap.has(question.id)) {
          followUpMap.set(question.id, new Set());
        }
        followUpMap.get(question.id).add(followUpId);
      }
    });
    
    return followUpMap;
  };
  
  // Check if a question is a follow-up question
  const isFollowUpQuestion = (questionId) => {
    const parentQuestions = getParentQuestions();
    const parentQuestionIds = parentQuestions.map(q => q.id);
    return !parentQuestionIds.includes(questionId);
  };
  
  const handleOpenDialog = (questionnaire = null) => {
    // Get only parent questions (not follow-up questions)
    const parentQuestions = getParentQuestions();
    
    if (questionnaire) {
      // Extract question IDs and category IDs from the questionnaire data
      const questionIds = questionnaire.questions ? 
        questionnaire.questions.map(q => q.id) : [];
      
      // Extract unique category IDs from the questions
      const categoryIds = questionnaire.questions ? 
        [...new Set(questionnaire.questions
          .filter(q => q.category_id)
          .map(q => q.category_id))] : [];
      
      // Process the questionnaire data
      const processedQuestionnaire = {
        ...questionnaire,
        questions: questionIds,
        categories: categoryIds,
        expiry_date: formatDateForInput(questionnaire.expiry_date)
      };
      
      setCurrentQuestionnaire(processedQuestionnaire);
      setSelectedCategories(categoryIds);
      setSelectedQuestions(questionIds);
    } else {
      // For new questionnaires, select all categories by default
      const allCategoryIds = categories.map(category => category.id);
      
      // For new questionnaires, select all parent questions by default
      const allParentQuestionIds = parentQuestions.map(question => question.id);
      
      setCurrentQuestionnaire({
        id: null,
        title: '',
        description: '',
        categories: allCategoryIds,
        questions: allParentQuestionIds,
        active: true,
        link_token: generateRandomToken(),
        expiry_date: getDefaultExpiryDate()
      });
      
      setSelectedCategories(allCategoryIds);
      setSelectedQuestions(allParentQuestionIds);
    }
    
    setActiveStep(0);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setActiveStep(0);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === 'active') {
      setCurrentQuestionnaire({ ...currentQuestionnaire, [name]: checked });
    } else {
      setCurrentQuestionnaire({ ...currentQuestionnaire, [name]: value });
    }
  };

  const handleCategoryToggle = (categoryId) => {
    const currentIndex = selectedCategories.indexOf(categoryId);
    const newSelectedCategories = [...selectedCategories];

    if (currentIndex === -1) {
      // Adding a category
      newSelectedCategories.push(categoryId);
    } else {
      // Removing a category
      newSelectedCategories.splice(currentIndex, 1);
      
      // When a category is unselected, remove all questions from that category
      const questionsToRemove = questions
        .filter(q => q.category_id === categoryId)
        .map(q => q.id);
      
      if (questionsToRemove.length > 0) {
        // Filter out questions from the unselected category
        const newSelectedQuestions = selectedQuestions.filter(id => !questionsToRemove.includes(id));
        setSelectedQuestions(newSelectedQuestions);
        console.log(`Removed ${questionsToRemove.length} questions from unselected category ${categoryId}`);
      }
    }

    setSelectedCategories(newSelectedCategories);
  };

  const handleQuestionToggle = (questionId) => {
    const currentIndex = selectedQuestions.indexOf(questionId);
    let newSelectedQuestions = [...selectedQuestions];
    const followUpMap = getFollowUpQuestionsMap();

    if (currentIndex === -1) {
      // Adding a question
      newSelectedQuestions.push(questionId);
      
      // Automatically include all follow-up questions for this question
      if (followUpMap.has(questionId)) {
        const followUpIds = Array.from(followUpMap.get(questionId));
        followUpIds.forEach(followUpId => {
          if (!newSelectedQuestions.includes(followUpId)) {
            newSelectedQuestions.push(followUpId);
            console.log(`Automatically added follow-up question ${followUpId} for question ${questionId}`);
          }
        });
      }
    } else {
      // Removing a question
      newSelectedQuestions.splice(currentIndex, 1);
      
      // Optionally: Remove follow-up questions that are only used by this question
      // This is more complex as we need to check if the follow-up is used by other selected questions
      // For now, we'll keep the follow-up questions to avoid breaking dependencies
    }

    setSelectedQuestions(newSelectedQuestions);
  };

  const handleNext = () => {
    // Validate based on current step
    if (activeStep === 0) {
      // Validate required fields for step 1
      if (!currentQuestionnaire.title.trim()) {
        setSnackbar({
          open: true,
          message: 'Please enter a title for the questionnaire',
          severity: 'error'
        });
        return;
      }
      
      // Validate expiry date
      if (!isValidExpiryDate(currentQuestionnaire.expiry_date)) {
        setSnackbar({
          open: true,
          message: 'Expiry date must be in the future',
          severity: 'error'
        });
        return;
      }
      
      // If moving from step 1 to step 2, filter questions based on selected categories
      const filteredQuestions = questions.filter(q => selectedCategories.includes(q.category_id));
      const filteredQuestionIds = filteredQuestions.map(q => q.id);
      
      // First, remove any questions that don't belong to selected categories
      // This ensures questions from unselected categories don't appear
      let validSelectedQuestions = selectedQuestions.filter(id => {
        const question = questions.find(q => q.id === id);
        // Keep the question if it belongs to a selected category
        return question && selectedCategories.includes(question.category_id);
      });
      
      // Also include follow-up questions even if they're in different categories
      const followUpMap = getFollowUpQuestionsMap();
      const followUpQuestions = new Set();
      
      // First pass: collect all direct follow-ups
      validSelectedQuestions.forEach(questionId => {
        if (followUpMap.has(questionId)) {
          const followUpIds = followUpMap.get(questionId);
          followUpIds.forEach(followUpId => followUpQuestions.add(followUpId));
        }
      });
      
      // Add the follow-up questions to the selected questions
      followUpQuestions.forEach(followUpId => {
        if (!validSelectedQuestions.includes(followUpId)) {
          validSelectedQuestions.push(followUpId);
          console.log(`Added follow-up question ${followUpId} from a different category`);
        }
      });
      
      setSelectedQuestions(validSelectedQuestions);
    } else if (activeStep === 1) {
      // Validate step 2 - ensure at least one question is selected
      if (selectedQuestions.length === 0) {
        setSnackbar({
          open: true,
          message: 'Please select at least one question for the questionnaire',
          severity: 'error'
        });
        return;
      }
    }
    
    // If validation passes, move to next step
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSaveQuestionnaire = async () => {
    try {
      // Final validation of expiry date
      if (!isValidExpiryDate(currentQuestionnaire.expiry_date)) {
        setSnackbar({
          open: true,
          message: 'Expiry date must be in the future',
          severity: 'error'
        });
        return;
      }
      
      // Ensure all follow-up questions are included
      let finalSelectedQuestions = [...selectedQuestions];
      const followUpMap = getFollowUpQuestionsMap();
      
      // Go through each selected question and add its follow-ups if not already included
      selectedQuestions.forEach(questionId => {
        if (followUpMap.has(questionId)) {
          const followUpIds = Array.from(followUpMap.get(questionId));
          followUpIds.forEach(followUpId => {
            if (!finalSelectedQuestions.includes(followUpId)) {
              finalSelectedQuestions.push(followUpId);
              console.log(`Added missing follow-up question ${followUpId} for question ${questionId}`);
            }
          });
        }
      });
      
      // Prepare the questionnaire data
      const questionnaireData = {
        ...currentQuestionnaire,
        categories: selectedCategories,
        questions: finalSelectedQuestions
      };
      
      // Ensure categories is an array
      if (!Array.isArray(questionnaireData.categories)) {
        questionnaireData.categories = [];
      }
      
      let savedQuestionnaire;
      
      if (currentQuestionnaire.id) {
        // Update existing questionnaire using the API
        savedQuestionnaire = await handleUpdateQuestionnaire(questionnaireData);
      } else {
        // Create new questionnaire using the API
        savedQuestionnaire = await handleCreateQuestionnaire(questionnaireData);
      }
      
      // Close the dialog and reset the form
      handleCloseDialog();
      setActiveStep(0);
      
      // Success message is now shown in the handleCreateQuestionnaire or handleUpdateQuestionnaire functions
    } catch (error) {
      console.error('Error saving questionnaire:', error);
    }
  };

  const handleDeleteQuestionnaire = async (id) => {
    try {
      // Delete the questionnaire using the API function
      await handleDeleteQuestionnaireAPI(id);
      
      // Success message (you could add a snackbar for this)
      console.log('Questionnaire deleted successfully');
    } catch (error) {
      console.error('Error deleting questionnaire:', error);
    }
  };

  const handleSendQuestionnaire = async (questionnaire, email) => {
    try {
      // Send the questionnaire using the API function
      await handleSendQuestionnaireEmail(questionnaire.id, email);
      
      // Clear the email input
      setEmailToSend('');
      
      // Success message (you could add a snackbar for this)
      console.log(`Questionnaire sent successfully to ${email}`);
    } catch (error) {
      console.error('Error sending questionnaire:', error);
    }
  };

  const handleCopyLink = (token) => {
    const link = `${window.location.origin}/questionnaire/${token}`;
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    
    // Hide the notification after 2 seconds
    setTimeout(() => {
      setLinkCopied(false);
    }, 2000);
  };

  // Helper functions
  const generateRandomToken = () => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  const getDefaultExpiryDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    return date.toISOString().split('T')[0];
  };
  
  // Format a date string to YYYY-MM-DD for input[type="date"]
  const formatDateForInput = (dateString) => {
    if (!dateString) return getDefaultExpiryDate();
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // Invalid date, return default
        return getDefaultExpiryDate();
      }
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error('Error formatting date:', error);
      return getDefaultExpiryDate();
    }
  };
  
  // Validate expiry date (must be in the future)
  const isValidExpiryDate = (dateString) => {
    try {
      const expiryDate = new Date(dateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to beginning of day for fair comparison
      
      return expiryDate >= today;
    } catch (error) {
      console.error('Error validating expiry date:', error);
      return false;
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Unknown Category';
  };

  const getQuestionTitle = (questionId) => {
    const question = questions.find(q => q.id === questionId);
    return question ? question.title : 'Unknown Question';
  };

  const getQuestionsCountByCategory = (categoryId) => {
    if (!questions || !Array.isArray(questions)) return 0;
    return questions.filter(q => q && q.category_id === categoryId).length;
  };

  const getSelectedQuestionsCountByCategory = (categoryId) => {
    if (!questions || !Array.isArray(questions) || !selectedQuestions || !Array.isArray(selectedQuestions)) return 0;
    
    // Get only parent questions for this category
    const parentQuestions = getParentQuestions();
    const categoryQuestionIds = parentQuestions
      .filter(q => q && q.category_id === categoryId)
      .map(q => q.id);
    
    return selectedQuestions.filter(id => id && categoryQuestionIds.includes(id)).length;
  };

  const steps = ['Basic Information', 'Select Categories', 'Select Questions', 'Review & Finalize'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Questionnaire Manager
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => handleOpenDialog()}
        >
          Create Questionnaire
        </Button>
      </Box>

      <Grid container spacing={3}>
        {questionnaires.map(questionnaire => (
          <Grid item xs={12} sm={6} md={4} key={questionnaire.id}>
            <Card>
              <CardContent>
                <Typography variant="h6" component="h2" gutterBottom>
                  {questionnaire.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {questionnaire.description}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`${questionnaire.questions ? questionnaire.questions.length : 0} Questions`} 
                    size="small" 
                    color="primary" 
                    variant="outlined" 
                  />
                  <Chip 
                    label={`${questionnaire.categories && Array.isArray(questionnaire.categories) ? questionnaire.categories.length : 0} Categories`} 
                    size="small" 
                    color="secondary" 
                    variant="outlined" 
                  />
                  <Chip 
                    label={questionnaire.active ? 'Active' : 'Inactive'} 
                    size="small" 
                    color={questionnaire.active ? 'success' : 'error'} 
                    variant="outlined" 
                  />
                </Box>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Share Link:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <OutlinedInput
                      size="small"
                      value={`${window.location.origin}/questionnaire/${questionnaire.link_token}`}
                      fullWidth
                      readOnly
                      endAdornment={
                        <InputAdornment position="end">
                          <Tooltip title="Copy link">
                            <IconButton
                              edge="end"
                              onClick={() => handleCopyLink(questionnaire.link_token)}
                              size="small"
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      }
                      sx={{ fontSize: '0.75rem' }}
                    />
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    size="small"
                    label="Email"
                    placeholder="Enter email to send"
                    value={emailToSend}
                    onChange={(e) => setEmailToSend(e.target.value)}
                    sx={{ flexGrow: 1, mr: 1 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={() => handleSendQuestionnaire(questionnaire, emailToSend)}
                    disabled={!emailToSend || !/\S+@\S+\.\S+/.test(emailToSend)}
                  >
                    Send
                  </Button>
                </Box>
              </CardContent>
              <CardActions>
                <IconButton size="small" onClick={() => handleOpenDialog(questionnaire)}>
                  <EditIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  component={Link} 
                  to={`/admin/preview/${questionnaire.id}`}
                >
                  <PreviewIcon />
                </IconButton>
                <IconButton size="small" onClick={() => handleDeleteQuestionnaire(questionnaire.id)}>
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentQuestionnaire.id ? 'Edit Questionnaire' : 'Create New Questionnaire'}
        </DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {activeStep === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="title"
                  label="Questionnaire Title"
                  value={currentQuestionnaire.title}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="Description"
                  value={currentQuestionnaire.description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={3}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="expiry_date"
                  label="Expiry Date"
                  type="date"
                  value={currentQuestionnaire.expiry_date}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ mt: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="active"
                        checked={currentQuestionnaire.active}
                        onChange={handleInputChange}
                      />
                    }
                    label="Active Questionnaire"
                  />
                </Box>
              </Grid>
            </Grid>
          )}
          
          {activeStep === 1 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Select Categories to Include
              </Typography>
              <FormGroup>
                {categories.map((category) => (
                  <FormControlLabel
                    key={category.id}
                    control={
                      <Checkbox
                        checked={selectedCategories.indexOf(category.id) !== -1}
                        onChange={() => handleCategoryToggle(category.id)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1">{category.name}</Typography>
                        <Chip 
                          label={`${getQuestionsCountByCategory(category.id)} Questions`} 
                          size="small" 
                          sx={{ ml: 1 }} 
                        />
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
              
              {selectedCategories.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Please select at least one category to continue.
                </Alert>
              )}
            </Box>
          )}
          
          {activeStep === 2 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Select Questions to Include
              </Typography>
              
              {(selectedCategories || []).map((categoryId) => {
                if (!questions || !Array.isArray(questions)) return null;
                
                // Get only parent questions (not follow-up questions) for this category
                const parentQuestions = getParentQuestions();
                const categoryQuestions = parentQuestions.filter(q => q && q.category_id === categoryId);
                
                return (
                  <Box key={categoryId} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2">
                        {getCategoryName(categoryId)}
                      </Typography>
                      <Chip 
                        label={`${getSelectedQuestionsCountByCategory(categoryId)}/${categoryQuestions.length} Selected`} 
                        size="small" 
                        color="primary" 
                      />
                    </Box>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <FormGroup>
                        {categoryQuestions.map((question) => (
                          <FormControlLabel
                            key={question.id}
                            control={
                              <Checkbox
                                checked={selectedQuestions.indexOf(question.id) !== -1}
                                onChange={() => handleQuestionToggle(question.id)}
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography>{question.title}</Typography>
                                {question.has_follow_up && (
                                  <Chip 
                                    label="Has Follow-ups" 
                                    size="small" 
                                    color="info" 
                                    variant="outlined"
                                    sx={{ ml: 1, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            }
                          />
                        ))}
                      </FormGroup>
                    </Paper>
                  </Box>
                );
              })}
              
              {selectedQuestions.length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Please select at least one question to continue.
                </Alert>
              )}
            </Box>
          )}
          
          {activeStep === 3 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Review Questionnaire
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {currentQuestionnaire.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {currentQuestionnaire.description}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Status:</strong> {currentQuestionnaire.active ? 'Active' : 'Inactive'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <strong>Expiry Date:</strong> {currentQuestionnaire.expiry_date}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
              
              <Typography variant="subtitle2" gutterBottom>
                Selected Categories ({selectedCategories && Array.isArray(selectedCategories) ? selectedCategories.length : 0})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {(selectedCategories || []).map((categoryId) => (
                  <Chip 
                    key={categoryId} 
                    label={getCategoryName(categoryId)} 
                    color="primary" 
                    variant="outlined" 
                  />
                ))}
              </Box>
              
              <Typography variant="subtitle2" gutterBottom>
                Selected Questions ({selectedQuestions && Array.isArray(selectedQuestions) ? selectedQuestions.length : 0})
              </Typography>
              <List dense>
                {(selectedQuestions || []).map((questionId) => (
                  <ListItem key={questionId}>
                    <ListItemText 
                      primary={getQuestionTitle(questionId)} 
                      secondary={getCategoryName(questions && Array.isArray(questions) ? (questions.find(q => q && q.id === questionId)?.category_id || 0) : 0)} 
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {activeStep > 0 && (
            <Button onClick={handleBack}>
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button 
              onClick={handleNext} 
              variant="contained"
              disabled={
                (activeStep === 0 && (!currentQuestionnaire || !currentQuestionnaire.title)) ||
                (activeStep === 1 && (!selectedCategories || !Array.isArray(selectedCategories) || selectedCategories.length === 0)) ||
                (activeStep === 2 && (!selectedQuestions || !Array.isArray(selectedQuestions) || selectedQuestions.length === 0))
              }
            >
              Next
            </Button>
          ) : (
            <Button 
              onClick={handleSaveQuestionnaire} 
              variant="contained"
              disabled={!selectedQuestions || !Array.isArray(selectedQuestions) || selectedQuestions.length === 0}
            >
              Save Questionnaire
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={linkCopied}
        autoHideDuration={2000}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuestionnaireManager;
